import { BufferGeometry, Vector3 } from "three";

interface PriceCalculation {
  materialVolume: number;
  materialCost: number;
  machineTime: number;
  laborCost: number;
  totalCost: number;
}

interface PrintMaterial {
  name: string;
  costPerKg: number;
  density: number; // g/cm³
}

// Define available filament materials
const PRINT_MATERIALS: Record<string, PrintMaterial> = {
  PLA: { name: "PLA", costPerKg: 20, density: 1.24 },
  ABS: { name: "ABS", costPerKg: 25, density: 1.04 },
  PETG: { name: "PETG", costPerKg: 28, density: 1.27 },
  TPU: { name: "TPU", costPerKg: 40, density: 1.21 }, // Example TPU
};

const DEFAULT_FILAMENT_NAME = "PLA";

const DEFAULT_INFILL = 0.2; // 20% infill
const MACHINE_COST_PER_HOUR = 5; // $5 per hour for machine usage
const LABOR_COST_PER_HOUR = 15; // $15 per hour for labor
const LAYER_HEIGHT = 0.2; // mm
// const PRINT_SPEED = 50; // mm/s

export function calculateVolume(geometry: BufferGeometry): number {
  const positions = geometry.attributes.position.array;
  let volume = 0;

  // Calculate volume using the shoelace formula for each triangle
  for (let i = 0; i < positions.length; i += 9) {
    const v1 = new Vector3(positions[i], positions[i + 1], positions[i + 2]);
    const v2 = new Vector3(
      positions[i + 3],
      positions[i + 4],
      positions[i + 5]
    );
    const v3 = new Vector3(
      positions[i + 6],
      positions[i + 7],
      positions[i + 8]
    );

    // Calculate the signed volume of the tetrahedron
    const signedVolume = v1.dot(v2.cross(v3)) / 6;
    volume += Math.abs(signedVolume);
  }

  // Convert from cubic units to cubic centimeters
  return volume / 1000;
}

export function estimatePrintTime(
  volume: number,
  layerHeight: number = LAYER_HEIGHT
): number {
  // This is a simplified estimation
  // In reality, you'd want to consider:
  // - Actual path length
  // - Acceleration/deceleration
  // - Travel moves
  // - Layer changes
  const height = Math.cbrt(volume) * 10; // Approximate height in mm
  const layers = height / layerHeight;
  const timePerLayer = 60; // seconds (simplified)

  return (layers * timePerLayer) / 3600; // Convert to hours
}

export function calculateCosts(
  geometry: BufferGeometry,
  filamentName: string = DEFAULT_FILAMENT_NAME,
  infill: number = DEFAULT_INFILL
): PriceCalculation {
  const material =
    PRINT_MATERIALS[filamentName] || PRINT_MATERIALS[DEFAULT_FILAMENT_NAME];

  const solidVolume = calculateVolume(geometry);
  // Ensure solidVolume is non-negative and finite
  if (!isFinite(solidVolume) || solidVolume < 0) {
    console.error("Invalid solid volume calculated:", solidVolume);
    // Return a zero-cost object or throw an error
    return {
      materialVolume: 0,
      materialCost: 0,
      machineTime: 0,
      laborCost: 0,
      totalCost: 0,
    };
  }
  const materialVolume = solidVolume * (1 + infill); // Account for infill

  // Convert volume to mass (g)
  const mass = materialVolume * material.density;

  // Calculate material cost
  const materialCost = (mass / 1000) * material.costPerKg;

  // Estimate print time
  const machineTime = estimatePrintTime(materialVolume);

  // Calculate costs
  const machineCost = machineTime * MACHINE_COST_PER_HOUR;
  const laborCost = machineTime * LABOR_COST_PER_HOUR;

  const totalCost = materialCost + machineCost + laborCost;

  return {
    materialVolume,
    materialCost,
    machineTime,
    laborCost,
    totalCost,
  };
}

// Helper to get available material names
export function getAvailableFilaments(): string[] {
  return Object.keys(PRINT_MATERIALS);
}
